import { neverError } from '@seu-helpers';
import {
  SeuCanvas,
  SeuCanvasStreamPositionItem,
  SeuComponent,
  SeuCoords,
} from '@seu-types';

export function getStreamPositionItem(
  streamArray: SeuCanvasStreamPositionItem[],
  coords: SeuCoords | null,
  handleId: string | null,
  side: 'input' | 'output'
): SeuCanvasStreamPositionItem | null {
  switch (side) {
    case 'output': {
      const outputStream = streamArray.find(
        (stream) =>
          stream.startX === coords?.x &&
          stream.startY === coords?.y &&
          stream.outputFromComponentHandleId === handleId
      );
      return outputStream ?? null;
    }
    case 'input': {
      const inputStream = streamArray.find(
        (stream) =>
          stream.endX === coords?.x &&
          stream.endY === coords?.y &&
          stream.inputForComponentHandleId === handleId
      );
      return inputStream ?? null;
    }
    default:
      neverError(side);
  }
}

export function getComponentNameByStream(
  components: SeuComponent[],
  stream: SeuCanvasStreamPositionItem,
  side: 'input' | 'output'
): string {
  let id =
    side === 'input'
      ? stream.inputForComponentId
      : stream.outputFromComponentId;
  if (side === 'input' && stream.linkedToChimney) {
    id = '-1';
  } else if (side === 'output' && stream.inputStreamId) {
    id = stream.inputStreamId;
  }

  return components.find((component) => component.id === id)?.name ?? '???';
}

export function isStreamBlockedByElements(
  stream: SeuCanvasStreamPositionItem,
  canvas: SeuCanvas
): boolean {
  if (stream.startY !== stream.endY) {
    return false; // Only straight horizontal streams can be disrupted
  }

  const componentsInPath = canvas.components.filter((component) => {
    if (component.posY !== stream.startY) {
      return false; // Component is not on the same row as the stream
    }
    return (
      (component.posX > stream.startX && component.posX < stream.endX) ||
      (component.posX > stream.endX && component.posX < stream.startX)
    );
  });

  const inputStreamsInPath = canvas.inputStreams.filter((inputStream) => {
    if (inputStream.posY !== stream.startY) {
      return false; // Input stream is not on the same row as the stream
    }
    return (
      (inputStream.posX > stream.startX && inputStream.posX < stream.endX) ||
      (inputStream.posX > stream.endX && inputStream.posX < stream.startX)
    );
  });

  const isChimneyInPath = canvas.chimney
    ? canvas.chimney.posY === stream.startY &&
      ((canvas.chimney.posX > stream.startX &&
        canvas.chimney.posX < stream.endX) ||
        (canvas.chimney.posX > stream.endX &&
          canvas.chimney.posX < stream.startX))
    : false;

  return (
    componentsInPath.length > 0 ||
    inputStreamsInPath.length > 0 ||
    isChimneyInPath
  );
}

export function hasStreamNaturalFlow(
  startX: number,
  endX: number,
  inputSide: 'left' | 'right',
  outputSide: 'left' | 'right'
): boolean {
  if (inputSide === outputSide) {
    return false; // If both handles are on the same side, it's not a natural flow
  }

  const deltaX = endX - startX;
  if (outputSide === 'right' && deltaX > 0) {
    return true; // Right output to left input with endX greater than startX is natural flow
  }
  if (outputSide === 'left' && deltaX < 0) {
    return true; // Left output to right input with endX less than startX is natural flow
  }
  return false;
}
